import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useWallets } from '@modules/Wallets/List/hooks/useWallets';
import { usePermissions } from '@shared/hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTopups } from '../../hooks/useTopups';
import { useTopupsColumns } from '../../hooks/useTopupsColumns';
import { Topup } from '../../models';
import { TopupsAgGridTable, TopupsBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { CreateTopupButton, FiltersSection, StatusToolbar } from '../interaction';
import {
  ApproveTopupModal,
  ApproveTopupModalRef,
  CreateTopupModal,
  CreateTopupModalRef,
  RejectTopupModal,
  RejectTopupModalRef,
  ViewTopupModal,
  ViewTopupModalRef,
} from '../modals';

const getColumnId = (columnId?: string, accessorKey?: string | number) => {
  if (columnId) return columnId;
  if (typeof accessorKey === 'string') return accessorKey;
  return String(accessorKey ?? '');
};

const matchesSearch = (topup: Topup, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();

  const searchableValues = [
    topup.id?.toString(),
    topup.status,
    topup.declared_amount?.toString(),
    topup.confirmed_amount?.toString(),
    topup.payment_method,
    topup.payment_reference,
    topup.payer_name,
    topup.payer_iban,
  ];

  return searchableValues.some((value) => value?.toString().toLowerCase().includes(normalized));
};

const buildAdvancedFiltersPayload = (conditions: FilterConditionOutput[], statusFilter?: string): any | undefined => {
  const filters = conditions
    .map((condition) => {
      const { column, action } = condition;
      const rawValue = condition.value as unknown;

      if (!column || !action) {
        return null;
      }

      const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in']);
      const VOID_OPERATORS = new Set(['is_null', 'is_not_null']);
      const NUMERIC_COLUMNS = new Set(['wallet_id']);

      if (VOID_OPERATORS.has(action)) {
        return {
          column,
          operator: action,
        };
      }

      if (MULTI_VALUE_OPERATORS.has(action)) {
        let valuesArray: Array<string | number> = [];

        if (Array.isArray(rawValue)) {
          valuesArray = rawValue as Array<string | number>;
        } else if (typeof rawValue === 'string') {
          valuesArray = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        } else if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          valuesArray = [rawValue as string | number];
        }

        if (!valuesArray.length) {
          return null;
        }

        const processedArray = NUMERIC_COLUMNS.has(column)
          ? valuesArray
              .map((item) => {
                const numericValue = typeof item === 'number' ? item : Number(item);
                return Number.isNaN(numericValue) ? null : numericValue;
              })
              .filter((item): item is number => item !== null)
          : valuesArray;

        if (!processedArray.length) {
          return null;
        }

        return {
          column,
          operator: action,
          value: processedArray,
        };
      }

      const singleValue = Array.isArray(rawValue) ? rawValue[0] : (rawValue as string | number | undefined);

      if (singleValue === undefined || singleValue === null || singleValue === '') {
        return null;
      }

      if (NUMERIC_COLUMNS.has(column)) {
        const numericValue = typeof singleValue === 'number' ? singleValue : Number(singleValue);
        if (Number.isNaN(numericValue)) {
          return null;
        }
        return {
          column,
          operator: action,
          value: numericValue,
        };
      }

      return {
        column,
        operator: action,
        value: singleValue,
      };
    })
    .filter((filter): filter is { column: string; operator: string; value?: any } => filter !== null);

  // Añadir filtro de status si está seleccionado
  if (statusFilter && statusFilter !== 'all') {
    filters.push({
      column: 'status',
      operator: 'in',
      value: [statusFilter],
    });
  }

  if (!filters.length) {
    return undefined;
  }

  return {
    filters,
    logic: 'AND',
  };
};

interface MainProps {
  walletIdProp?: number | null;
  embedded?: boolean;
}

export const Main = ({ walletIdProp = null, embedded = false }: MainProps) => {
  const { t } = useTranslation('topupsModule');
  const { canAccess } = usePermissions();
  const canCreate = canAccess(['tenant_3pl', 'tenant_external'], ['topups.create']);
  const is3pl = canAccess(['tenant_3pl'], []);
  const isExternal = canAccess(['tenant_external'], []);

  const [walletId, setWalletId] = useState<number | null>(walletIdProp);
  const [searchValue, setSearchValue] = useState('');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);

  const advancedFilters = useMemo(
    () => buildAdvancedFiltersPayload(filterConditions, selectedStatus),
    [filterConditions, selectedStatus],
  );

  const topupsFilters = useMemo(
    () =>
      advancedFilters
        ? { advanced_filters: advancedFilters, wallet_id: walletId ?? undefined }
        : { wallet_id: walletId ?? undefined },
    [advancedFilters, walletId],
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useTopups(walletId, topupsFilters);
  const { data: walletsData, isLoading: walletsLoading } = useWallets(
    isExternal && !embedded ? { perPage: 1 } : undefined,
  );

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, isLoading]);

  // Auto-seleccionar wallet para external en vista general
  useEffect(() => {
    if (!isExternal) return;
    if (embedded) return;
    if (walletId) return;
    const firstWalletId = walletsData?.data?.[0]?.id;
    if (firstWalletId) {
      setWalletId(firstWalletId);
    }
  }, [isExternal, embedded, walletId, walletsData?.data]);

  const createModalRef = useRef<CreateTopupModalRef>(null);
  const approveModalRef = useRef<ApproveTopupModalRef>(null);
  const rejectModalRef = useRef<RejectTopupModalRef>(null);
  const viewModalRef = useRef<ViewTopupModalRef>(null);

  const handleOpenCreate = useCallback(() => {
    if (!walletId) return;
    createModalRef.current?.open(walletId);
  }, [walletId]);

  const handleApprove = useCallback((t: Topup) => {
    approveModalRef.current?.open(t);
  }, []);

  const handleReject = useCallback((t: Topup) => {
    rejectModalRef.current?.open(t);
  }, []);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const { tanStackColumns, filteredColumnDefs } = useTopupsColumns({
    onApprove: is3pl ? handleApprove : undefined,
    onReject: is3pl ? handleReject : undefined,
    onViewDetail: (t) => viewModalRef.current?.open(t),
    selectedColumns,
  });

  const columnIds = useMemo(
    () =>
      tanStackColumns.map((column) =>
        getColumnId(
          column.id as string,
          Object.prototype.hasOwnProperty.call(column, 'accessorKey')
            ? (column as { accessorKey?: string | number }).accessorKey
            : undefined,
        ),
      ),
    [tanStackColumns],
  );

  useEffect(() => {
    setSelectedColumns((current) => {
      if (current.length === columnIds.length && current.every((id) => columnIds.includes(id))) {
        return current;
      }
      return columnIds;
    });
  }, [columnIds]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleFilterChange = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const handleColumnsChange = useCallback((columnsIds: string[]) => {
    setSelectedColumns(columnsIds);
  }, []);

  const handleStatusChange = useCallback((status?: string) => {
    setSelectedStatus(status);
  }, []);

  const topups = useMemo(() => data?.data ?? [], [data]);

  const filteredTopups = useMemo(() => topups.filter((t) => matchesSearch(t, searchValue)), [topups, searchValue]);

  const hasActiveFilters = Boolean(searchValue?.trim()) || filterConditions.length > 0;
  const isRefetching = isFetching && !isInitialLoad;

  if (isInitialLoad || walletsLoading) {
    return <LoadingState message={t('loading.topups')} />;
  }

  if (isError || !data) {
    return <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} title={t('errors.title')} />;
  }

  return (
    <>
      <TopupsBreadcrumb />
      <div className="px-6 pt-4">
        <div className="shrink-0 absolute right-4 top-2 flex items-center gap-2">
          {canCreate && walletId && <CreateTopupButton onClick={handleOpenCreate} />}
        </div>

        {/* Header con filtros y botón crear */}
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <FiltersSection
              columns={tanStackColumns as ColumnDef<Topup>[]}
              selectedColumns={selectedColumns}
              fixedColumns={['actions']}
              onColumnsChange={handleColumnsChange}
              searchValue={searchValue}
              onSearch={handleSearch}
              filterConditions={filterConditions}
              onFilterChange={handleFilterChange}
            />

            <div className="flex flex-col items-end ml-auto">
              <TotalResults total={filteredTopups.length} />
            </div>
          </div>
          {/* Tabs de status */}
          {!isLoading && (
            <StatusToolbar onStatusChange={handleStatusChange} active={activeTab} setActive={setActiveTab} />
          )}
        </div>

        {filteredTopups.length === 0 ? (
          <EmptyView
            type={hasActiveFilters ? 'noResults' : 'emptyState'}
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={hasActiveFilters ? t('noResults') : t('emptyState')}
          />
        ) : (
          <div
            className={`transition-opacity duration-300 ${
              isRefetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            <TopupsAgGridTable topups={filteredTopups} columnDefs={filteredColumnDefs} />
          </div>
        )}
      </div>

      <CreateTopupModal ref={createModalRef} onSuccess={refetch} />
      <ApproveTopupModal ref={approveModalRef} onSuccess={refetch} />
      <RejectTopupModal ref={rejectModalRef} onSuccess={refetch} />
      <ViewTopupModal ref={viewModalRef} />
    </>
  );
};
